import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import CardGuide from './CardGuide';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import IndicatorTextarea, { IndicatorTextareaProps } from './IndicatorTextarea';
import { Indicator, IndicatorSectionKey, IndicatorWithUpdate } from './utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';
import { getMadLibForIndicator } from './IndicatorMadLibs';
import { ReportPeriod } from '../SectionPeriod/utils';
import { formattedDate } from 'shared/utils/date';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { IndicatorDetails } from '../IndicatorDetails/utils';
import { lowerCaseFirstLetter } from 'shared/utils/stringUtils';
import { getExampleText } from '../ImpactSummary/IndicatorGhostText';
import { directRegex } from '../SectionMultiRadio';
import { serviceGoalMapping } from './ServiceGoalMapping';

const replacePlaceholdersWithJSX = (
  element: string,
  programName: JSX.Element | string | undefined,
): JSX.Element | string | undefined => {
  if (!programName) return element;
  if (typeof programName === 'string') {
    return element.replace(/{{programName}}/g, programName);
  }
  if (typeof element === 'string') {
    const parts = element.split(/({{programName}})/g);
    return (
      <>
        {parts.map((part, index) => {
          if (part === '{{programName}}') {
            return React.cloneElement(programName, { key: index });
          }
          return part;
        })}
      </>
    );
  }
  return <>{element}</>;
};

export const replaceMadLib = (
  sentence: string,
  programName: JSX.Element | string | undefined,
  beneficiaryTerm: string | undefined,
  intermediaries: string | undefined,
  isReportPassed: boolean | undefined,
  reportPeriod: ReportPeriod | undefined,
  serviceGoal: string | undefined,
  reachHumanIndicator: IndicatorDetails | undefined,
  learnIndicator: IndicatorDetails | undefined,
  actIndicator: IndicatorDetails | undefined,
  forecast: string | number | undefined,
  finalForecast: string | number | undefined,
  indicator: IndicatorWithUpdate | Indicator | undefined,
) => {
  const startDate = reportPeriod?.startDate
    ? formattedDate(reportPeriod.startDate)
    : undefined;
  const endDate = reportPeriod?.endDate
    ? formattedDate(reportPeriod.endDate)
    : undefined;
  if (beneficiaryTerm) {
    sentence = sentence.replace(/{{beneficiaryTerm}}/g, beneficiaryTerm);
  }
  if (intermediaries) {
    sentence = sentence.replace(/{{intermediaries}}/g, intermediaries);
  }
  if (startDate && endDate) {
    sentence = sentence.replace(/{{startDate}}/g, startDate);
    sentence = sentence.replace(/{{endDate}}/g, endDate);
  } else {
    sentence = sentence.replace(/ from {{startDate}} to {{endDate}}/g, '');
  }
  if (serviceGoal) {
    sentence = sentence.replace(
      /{{serviceGoal}}/g,
      serviceGoalMapping[serviceGoal as keyof typeof serviceGoalMapping] ||
        serviceGoal,
    );
  }
  if (reachHumanIndicator?.forecast || reachHumanIndicator?.finalForecast) {
    sentence = sentence.replace(
      /{{reach-human #}}/g,
      numberWithCommas(
        reachHumanIndicator.forecast?.toString() ||
          reachHumanIndicator?.finalForecast?.toString(),
      ),
    );
  }
  if (sentence.includes('{{learned OR were reached}}')) {
    sentence = sentence.replace(
      /{{learned OR were reached}}/g,
      learnIndicator ? 'learned' : 'were reached',
    );
  }
  if (sentence.includes('{{act # OR learn # OR reach #}}')) {
    if (actIndicator?.forecast || actIndicator?.finalForecast) {
      sentence = sentence.replace(
        /{{act # OR learn # OR reach #}}/g,
        numberWithCommas(
          actIndicator.forecast?.toString() ||
            actIndicator?.finalForecast?.toString(),
        ),
      );
    } else if (learnIndicator?.forecast || learnIndicator?.finalForecast) {
      sentence = sentence.replace(
        /{{act # OR learn # OR reach #}}/g,
        numberWithCommas(
          learnIndicator.forecast?.toString() ||
            learnIndicator?.finalForecast?.toString(),
        ),
      );
    } else if (
      reachHumanIndicator?.forecast ||
      reachHumanIndicator?.finalForecast
    ) {
      sentence = sentence.replace(
        /{{act # OR learn # OR reach #}}/g,
        numberWithCommas(
          reachHumanIndicator.forecast?.toString() ||
            reachHumanIndicator?.finalForecast?.toString(),
        ),
      );
    }
  }
  if (sentence.includes('{{learn # OR reach #}}')) {
    if (learnIndicator?.forecast || learnIndicator?.finalForecast) {
      sentence = sentence.replace(
        /{{learn # OR reach #}}/g,
        numberWithCommas(
          learnIndicator.forecast?.toString() ||
            learnIndicator?.finalForecast?.toString(),
        ),
      );
    } else if (
      reachHumanIndicator?.forecast ||
      reachHumanIndicator?.finalForecast
    ) {
      sentence = sentence.replace(
        /{{learn # OR reach #}}/g,
        numberWithCommas(
          reachHumanIndicator.forecast?.toString() ||
            reachHumanIndicator?.finalForecast?.toString(),
        ),
      );
    }
  }
  if (sentence.includes('{{learned # OR reached #}}')) {
    if (learnIndicator?.finalForecast) {
      sentence = sentence.replace(
        /{{learned # OR reached #}}/g,
        numberWithCommas(learnIndicator.finalForecast.toString()),
      );
    } else if (reachHumanIndicator?.finalForecast) {
      sentence = sentence.replace(
        /{{learned # OR reached #}}/g,
        numberWithCommas(reachHumanIndicator.finalForecast.toString()),
      );
    }
  }
  if (forecast) {
    sentence = sentence.replace(
      /{{forecast}}/g,
      numberWithCommas(forecast.toString() || finalForecast?.toString()),
    );
  }
  if (finalForecast) {
    sentence = sentence.replace(
      /{{finalForecast}}/g,
      numberWithCommas(finalForecast.toString()),
    );
  }
  if (sentence.includes('{{took action OR learned OR were reached}}')) {
    sentence = sentence.replace(
      /{{took action OR learned OR were reached}}/g,
      actIndicator
        ? 'took action'
        : learnIndicator
        ? 'learned'
        : 'were reached',
    );
  }
  if (sentence.includes('{{learn OR reach}}')) {
    sentence = sentence.replace(
      /{{learn OR reach}}/g,
      learnIndicator
        ? isReportPassed
          ? 'learned'
          : 'learn'
        : isReportPassed
        ? 'reached'
        : 'reach',
    );
  }
  if (sentence.includes('{{intermediaries OR beneficiaryTerm}}')) {
    sentence = sentence.replace(
      /{{intermediaries OR beneficiaryTerm}}/g,
      intermediaries?.toLowerCase() ||
        beneficiaryTerm?.toLowerCase() ||
        'beneficiaries',
    );
  }
  if (programName) {
    return replacePlaceholdersWithJSX(sentence, programName);
  }
};

export const getSentenceStructureUnit = (
  sectionKey: IndicatorSectionKey | undefined,
  indicator: IndicatorWithUpdate | undefined,
  beneficiaryTerm: string | undefined,
  intermediaries: string | undefined,
  serviceType: string | undefined,
) => {
  if (sectionKey === 'capacityDevelopment') {
    return indicator?.unit;
  }
  if (sectionKey === 'succeed') {
    return indicator?.isHuman
      ? beneficiaryTerm
      : indicator?.unit || beneficiaryTerm;
  }
  const isDirect = serviceType ? directRegex.test(serviceType) : undefined;
  if (sectionKey === 'reach' || isDirect === undefined) {
    return indicator?.unit || intermediaries || beneficiaryTerm;
  }
  if (isDirect) {
    return beneficiaryTerm || indicator?.unit || intermediaries;
  }
  return intermediaries || indicator?.unit || beneficiaryTerm;
};

export const getSentenceStructure = (
  indicator: IndicatorWithUpdate | undefined,
  beneficiaryTerm: string | undefined,
  intermediaries: string | undefined,
  sectionKey: IndicatorSectionKey | undefined,
  start: string | undefined,
  end: string | undefined,
  serviceType: string | undefined,
): string | undefined => {
  const unit = getSentenceStructureUnit(
    sectionKey,
    indicator,
    beneficiaryTerm,
    intermediaries,
    serviceType,
  );
  if (!unit || !start || !end) {
    return undefined;
  }
  return `${start} ${
    indicator?.id === 10032 && unit === 'Animals'
      ? 'people'
      : lowerCaseFirstLetter(unit)
  } ${end}`;
};

const getCardMadLib = (
  indicator: IndicatorWithUpdate,
  beneficiaryTerm: string | undefined,
  isReportPassed: boolean,
  sectionKey: IndicatorSectionKey,
  programName: JSX.Element | undefined,
  intermediaries: string | undefined,
  reportPeriod: ReportPeriod | undefined,
  serviceGoal: string | undefined,
  serviceType: string | undefined,
): string | JSX.Element | undefined => {
  const sentence = getSentenceStructure(
    indicator,
    beneficiaryTerm,
    intermediaries,
    sectionKey,
    indicator.definitionStart,
    indicator.definitionEnd,
    serviceType,
  );
  return sentence
    ? replaceMadLib(
        sentence,
        programName,
        beneficiaryTerm,
        intermediaries,
        isReportPassed,
        reportPeriod,
        serviceGoal,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        indicator,
      )
    : undefined;
};

export const getCardTitle = (
  indicator: IndicatorWithUpdate,
  beneficiaryTerm: string | undefined,
  isReportPassed: boolean,
  sectionKey: IndicatorSectionKey,
  programName: string | undefined,
  intermediaries: string | undefined,
  reportPeriod: ReportPeriod | undefined,
  serviceGoal: string | undefined,
  serviceType: string | undefined,
): string | JSX.Element => {
  if (!indicator.customizable) {
    return <>“{indicator.name}” is standardized and isn&#8217;t editable.</>;
  }
  // return `What do you mean by “${indicator.name}”?`;
  const defaultText = `What do you mean by “${indicator.name}”?`;
  const madLib = getMadLibForIndicator(indicator);
  if (!beneficiaryTerm || !madLib) return defaultText;
  const lowerCaseBeneficiaryTerm = beneficiaryTerm.toLowerCase();
  const wrappedProgramName = (
    <span className="program-name">{programName}</span>
  );

  const madLibFromBackend = getCardMadLib(
    indicator,
    beneficiaryTerm,
    isReportPassed,
    sectionKey,
    wrappedProgramName,
    intermediaries,
    reportPeriod,
    serviceGoal,
    serviceType,
  );
  if (madLibFromBackend) return madLibFromBackend;

  const unit =
    intermediaries ||
    (madLib.unit === 'people'
      ? lowerCaseBeneficiaryTerm === 'animals'
        ? 'people'
        : lowerCaseBeneficiaryTerm
      : madLib.unit);
  switch (sectionKey) {
    case 'capacityDevelopment':
      if (isReportPassed) {
        return (
          <TrueImpactText variant="heading-subheading" className="card-title">
            Define what "{unit} {madLib.finalOutcome}" means to{' '}
            {wrappedProgramName}.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText variant="heading-subheading" className="card-title">
          Define what "{unit} {madLib.forecastOutcome}" means to{' '}
          {wrappedProgramName}.
        </TrueImpactText>
      );
    case 'reach':
      if (isReportPassed) {
        return (
          <TrueImpactText variant="heading-subheading" className="card-title">
            {wrappedProgramName} considers{' '}
            {indicator.unit?.toLowerCase() || unit} successfully "reached" when
            the following occurred ...
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText variant="heading-subheading" className="card-title">
          {wrappedProgramName} considers {indicator.unit?.toLowerCase() || unit}{' '}
          successfully "reached" when the following occurs ...
        </TrueImpactText>
      );
    case 'learn':
      if (isReportPassed) {
        return (
          <TrueImpactText variant="heading-subheading" className="card-title">
            {wrappedProgramName} considers {unit} to have successfully gained
            skills, knowledge, or motivation when the following occurred ...
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText variant="heading-subheading" className="card-title">
          {wrappedProgramName} considers {unit} to have successfully gained
          skills, knowledge, or motivation when the following occurs ...
        </TrueImpactText>
      );
    case 'act':
      if (isReportPassed) {
        return (
          <TrueImpactText variant="heading-subheading" className="card-title">
            {wrappedProgramName} considers {unit} to have successfully taken
            action or changed behavior when the following occurred ...
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText variant="heading-subheading" className="card-title">
          {wrappedProgramName} considers {unit} to have successfully taken
          action or changed behavior when the following occurs ...
        </TrueImpactText>
      );
    case 'succeed':
      if (isReportPassed) {
        return (
          <TrueImpactText variant="heading-subheading" className="card-title">
            {wrappedProgramName} considers {unit} "{madLib.finalOutcome}"
            successfully when the following occurred ...
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText variant="heading-subheading" className="card-title">
          {wrappedProgramName} considers "{unit} {madLib.forecastOutcome}"
          successfully when the following occurs ...
        </TrueImpactText>
      );
    default:
      if (isReportPassed) {
        return (
          <TrueImpactText variant="heading-page" className="card-title">
            Define what "{unit} {madLib.finalOutcome}" means to your
            organization.;
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText variant="heading-page" className="card-title">
          Define what "{unit} {madLib.forecastOutcome}" means to your
          organization.;
        </TrueImpactText>
      );
  }
};

export interface IndicatorCardDetailsProps extends IndicatorTextareaProps {
  maxWidth?: number;
  hideDelete?: boolean;
  className?: string;
  isReportPassed: boolean;
  reportPeriod: ReportPeriod;
  onDelete?: (indicator: IndicatorWithUpdate, index: number) => unknown;
  handleIndicatorSeenUpdate: (
    sectionKey: IndicatorSectionKey,
    seen: boolean,
    index: number,
  ) => unknown;
}

const PADDING = `padding-left: ${SPACERS.extraLarge}; padding-right: ${SPACERS.extraLarge};`;

const StyledDiv = styled.div`
  border-radius: 4px;
  margin-bottom: ${SPACERS.large};
  background: ${COLORS.grayLightest};
  border: 1px solid ${COLORS.grayLight};
  padding-top: ${SPACERS.medium};
  padding-bottom: ${SPACERS.medium};
  .card-title,
  .card-description,
  .delete-button-wrapper {
    ${PADDING}
  }
  .card-title {
    .program-name {
      font-style: italic;
    }
  }
  .delete-button-wrapper {
    .delete-button {
      cursor: pointer;
      color: ${COLORS.ctaPrimary};
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const IndicatorCardDetails: React.FC<IndicatorCardDetailsProps> = ({
  index,
  reportId,
  indicator,
  sectionKey,
  hideDelete,
  className,
  serviceType,
  isReportPassed,
  serviceGoal,
  intermediaries,
  reportPeriod,
  beneficiaryTerm,
  programName,
  onDelete,
  handleIndicatorTextUpdate,
  handleIndicatorSeenUpdate,
}) => {
  const client = useClient();
  const [, makeRequest] = useGraphQLRequest<unknown>();
  const title = getCardTitle(
    indicator,
    beneficiaryTerm,
    isReportPassed,
    sectionKey,
    programName,
    intermediaries,
    reportPeriod,
    serviceGoal,
    serviceType,
  );

  // const markAsSeenRequest = useCallback(
  //   (indicatorId: number) => {
  //     makeRequest(
  //       updateSuccessCriteria(client!, reportId, indicatorId, true, undefined),
  //     );
  //   },
  //   [reportId, makeRequest],
  // );

  const handleDelete = useCallback(() => {
    onDelete?.(indicator, index);
  }, [onDelete, indicator, index]);

  // useEffect(() => {
  //   if (indicator.seen) return;
  //   markAsSeenRequest(indicator.id);
  //   handleIndicatorSeenUpdate(sectionKey, true, index);
  // }, [indicator.id, indicator.seen, index, handleIndicatorSeenUpdate]);

  return (
    <StyledDiv className={className}>
      <TrueImpactText variant="heading-subheading" className="card-title">
        {title}
      </TrueImpactText>
      <IndicatorTextarea
        index={index}
        reportId={reportId}
        indicator={indicator}
        programName={programName}
        serviceGoal={serviceGoal}
        serviceType={serviceType}
        intermediaries={intermediaries}
        sectionKey={sectionKey}
        className="card-description"
        beneficiaryTerm={beneficiaryTerm}
        handleIndicatorTextUpdate={handleIndicatorTextUpdate}
      />
      {indicator.customizable && (
        <CardGuide
          text={getExampleText(indicator, sectionKey, beneficiaryTerm)}
          padding={PADDING}
        />
      )}
      {!hideDelete && (
        <TrueImpactText className="delete-button-wrapper">
          {indicator.lockDelete ? (
            <>This indicator can't be deleted.</>
          ) : (
            <TrueImpactText variant="body-small" align="center">
              Not needed?{' '}
              <span onClick={handleDelete} className="delete-button">
                Delete.
              </span>
            </TrueImpactText>
          )}
        </TrueImpactText>
      )}
    </StyledDiv>
  );
};

export default IndicatorCardDetails;
